import { useContext, useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, ErrorOutline, OpenInNew, VerifiedUser, WarningAmber } from '@mui/icons-material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { listCuitProfiles } from '@/api/cuitProfiles';
import { getActiveCertificate, testWsaa } from '@/api/certificates';
import type { CertificateResponse, CuitProfileResponse } from '@/api/types';
import { ProductContext } from '@/auth/ProductContext';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatCuit, formatDate } from '@/lib/formatters';
import { AFIP_ENVIRONMENT_SHORT, CERTIFICATE_STATUS_COLOR, CERTIFICATE_STATUS_LABEL } from '@/lib/labels';
import { CERT_EXPIRY_SOON_DAYS, daysUntil, expiryBucket, expiryLabel, type CertExpiryBucket } from '@/lib/certExpiry';
import { filterCuitProfilesForCommercialProduct } from '@/lib/cuitProfileScope';

export const Route = createFileRoute('/_authenticated/certificados/')({
  component: CertificadosPage,
});

const BUCKET_COLOR: Record<CertExpiryBucket, 'success' | 'warning' | 'error' | 'default'> = {
  ok: 'success',
  soon: 'warning',
  expired: 'error',
  none: 'default',
};

interface CertRow {
  profile: CuitProfileResponse;
  cert: CertificateResponse | null;
  bucket: CertExpiryBucket;
  days: number | null;
}

function CertificadosPage(): JSX.Element {
  const navigate = useNavigate();
  const product = useContext(ProductContext);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const includeDelegated = product?.activeProduct === 'Estudio';

  const profilesQ = useQuery({
    queryKey: ['cuit-profiles', 'all', includeDelegated],
    queryFn: () => listCuitProfiles(0, 200, { includeDelegated }),
  });

  const rawProfiles = profilesQ.data?.items ?? [];
  const profiles = useMemo(
    () => filterCuitProfilesForCommercialProduct(rawProfiles, product?.activeCommercialProduct),
    [product?.activeCommercialProduct, rawProfiles],
  );
  const isOwnCuitProduct = product?.activeCommercialProduct === 'Monotributo'
    || product?.activeCommercialProduct === 'ResponsableInscripto';

  const certQueries = useQueries({
    queries: profiles.map((p) => ({
      queryKey: ['cert-active', p.id],
      queryFn: () => getActiveCertificate(p.id),
      staleTime: 60_000,
    })),
  });

  const rows: CertRow[] = useMemo(() => {
    const list = profiles.map((profile, i) => {
      const cert = certQueries[i]?.data ?? null;
      return {
        profile,
        cert,
        bucket: expiryBucket(cert?.notAfter),
        days: daysUntil(cert?.notAfter),
      };
    });
    // Orden: lo más urgente primero (vencidos, luego por vencer); sin-cert al final.
    return list.sort((a, b) => {
      const av = a.days ?? Number.POSITIVE_INFINITY;
      const bv = b.days ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
  }, [profiles, certQueries]);

  const certsLoading = certQueries.some((q) => q.isLoading);
  const counts = {
    ok: rows.filter((r) => r.bucket === 'ok').length,
    soon: rows.filter((r) => r.bucket === 'soon').length,
    expired: rows.filter((r) => r.bucket === 'expired').length,
    none: rows.filter((r) => r.bucket === 'none').length,
  };

  const testMut = useMutation({
    mutationFn: (profileId: string) => testWsaa(profileId),
    onMutate: (profileId) => setTestingId(profileId),
    onSuccess: (res) => {
      setToast({
        message: res.success ? `WSAA OK · ${res.latencyMs} ms` : `WSAA falló: ${res.message}`,
        severity: res.success ? 'success' : 'error',
      });
    },
    onError: (err) => setToast({ message: toApiError(err).message, severity: 'error' }),
    onSettled: () => setTestingId(null),
  });

  if (profilesQ.isLoading) return <LoadingScreen message="Cargando certificados…" />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser color="primary" /> Certificados ARCA
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isOwnCuitProduct
              ? 'Controlá el certificado de tu CUIT en producción y homologación.'
              : 'Centro de certificados: vencimientos, renovación y validación WSAA por CUIT.'}
          </Typography>
        </Box>
      </Stack>

      {profilesQ.error ? (
        <MuiAlert severity="error">{toApiError(profilesQ.error).message}</MuiAlert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <SummaryCard label="Vigentes" value={counts.ok} color="success" icon={<CheckCircle color="success" />} />
            <SummaryCard label={`Por vencer (${CERT_EXPIRY_SOON_DAYS}d)`} value={counts.soon} color="warning" icon={<WarningAmber color="warning" />} />
            <SummaryCard label="Vencidos" value={counts.expired} color="error" icon={<ErrorOutline color="error" />} />
            <SummaryCard label="Sin certificado" value={counts.none} color="default" icon={<VerifiedUser color="disabled" />} />
          </Grid>

          {certsLoading && <LinearProgress sx={{ mb: 1 }} />}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>CUIT / razón social</TableCell>
                  <TableCell>Ambiente</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell align="right">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <CertRowView
                    key={r.profile.id}
                    row={r}
                    testing={testingId === r.profile.id}
                    onTest={() => testMut.mutate(r.profile.id)}
                    onOpen={() => void navigate({ to: '/cuit-profiles/$id', params: { id: r.profile.id } })}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: JSX.Element;
}): JSX.Element {
  return (
    <Grid item xs={6} md={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {icon}
          <Box>
            <Typography variant="h5">{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
}

function CertRowView({ row, testing, onTest, onOpen }: { row: CertRow; testing: boolean; onTest: () => void; onOpen: () => void }): JSX.Element {
  const { profile, cert, bucket, days } = row;
  // Barra de vida del cert: proporción del año restante (acotada 4–100%).
  const pct = days === null ? 0 : Math.max(4, Math.min(100, (days / 365) * 100));
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{profile.razonSocial}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatCuit(profile.cuit)}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" variant="outlined" label={AFIP_ENVIRONMENT_SHORT[profile.environment]} />
      </TableCell>
      <TableCell>
        {cert ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip size="small" variant="outlined" color={CERTIFICATE_STATUS_COLOR[cert.status]} label={CERTIFICATE_STATUS_LABEL[cert.status]} />
            {cert.validatedAt && (
              <Tooltip title={`WSAA validado ${formatDate(cert.validatedAt)}`}>
                <CheckCircle color="success" sx={{ fontSize: 16 }} />
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Chip size="small" variant="outlined" label="Sin certificado" />
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        {cert ? (
          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">{formatDate(cert.notAfter)}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{expiryLabel(cert.notAfter)}</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={pct} color={BUCKET_COLOR[bucket] === 'default' ? 'inherit' : BUCKET_COLOR[bucket]} sx={{ mt: 0.5, borderRadius: 1 }} />
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {cert && (
            <Button size="small" onClick={onTest} disabled={testing} startIcon={testing ? <CircularProgress size={14} /> : undefined}>
              Probar WSAA
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={onOpen} startIcon={<OpenInNew sx={{ fontSize: 16 }} />}>
            {cert ? 'Ver' : 'Cargar'}
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
